<template>
  <div
    class="-mt-32 mb-8 w-full bg-[url('https://images.rafflebox.ca/RaffleboxLogos/WebsiteAssets/space-for-everyone-bg-2.svg')] bg-cover bg-no-repeat px-7 pt-32 pb-24 [background-position-x:-80px] md:-mt-28 md:[background-position-x:0px] lg:-mt-28 lg:px-20 lg:py-96"
  >
    <!-- Desktop -->
    <div class="mx-auto hidden max-w-screen items-center justify-between gap-6 lg:flex">
      <div class="flex w-[34%] flex-col gap-10">
        <h3 class="text-white">{{ t('HomeFundraisingFor.heading') }}</h3>
        <Accordion v-model:active-index="activeIndex" @tab-open="handleTabOpen">
          <AccordionPanel v-for="(industry, index) in fundraiseForIndustries" :key="industry.name" :value="index">
            <AccordionHeader
              class="rounded-none border-x-0 border-t-2 border-b-0 border-solid border-zinc-400 bg-transparent px-0"
              :pt="{ toggleIcon: { class: 'text-white' } }"
            >
              <p class="font-family-display text-2xl font-medium text-white">{{ industry.name }}</p>
            </AccordionHeader>
            <AccordionContent :pt="{ content: { class: 'px-0 pt-0 pb-8 bg-transparent border-0 rounded-none' } }">
              <p class="text-lg text-white">{{ industry.description }}</p>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>
      </div>

      <div
        v-if="fundraiseForIndustries[lastOpenedIndex]"
        class="my-16 flex h-fit max-w-screen-sm items-center rounded-xl"
        :class="fundraiseForIndustries[lastOpenedIndex]?.bgColor"
      >
        <img
          :src="
            renderImage(`${fundraiseForIndustries[lastOpenedIndex]?.quote.imageUrl}`, {
              quality: 100,
              width: 260,
              height: 280,
            })
          "
          :alt="fundraiseForIndustries[lastOpenedIndex]?.quote.location"
          class="m-5 h-70 w-75 rounded-xl object-cover"
        />

        <div class="flex flex-col p-6">
          <p class="text-lg">{{ fundraiseForIndustries[lastOpenedIndex]?.quote.body }}</p>
          <p v-if="fundraiseForIndustries[lastOpenedIndex]?.quote.profession" class="mt-10 mb-1.5">
            {{ fundraiseForIndustries[lastOpenedIndex]?.quote.profession }}
          </p>
          <p v-if="fundraiseForIndustries[lastOpenedIndex]?.quote.location" class="text-xs italic">
            {{ fundraiseForIndustries[lastOpenedIndex]?.quote.location }}
          </p>
        </div>
      </div>
    </div>

    <!-- Mobile -->
    <div class="mx-auto flex max-w-87.5 flex-col items-center justify-center gap-10 lg:hidden">
      <h3 class="text-white">{{ t('HomeFundraisingFor.heading') }}</h3>
      <Carousel
        v-model:page="currentPage"
        :value="fundraiseForIndustries"
        :num-visible="1"
        :num-scroll="1"
        :circular="true"
        :show-indicators="true"
        :show-navigators="false"
        class="w-full"
        :pt="{
          indicatorList: { class: 'flex gap-5' },
          indicator: { class: 'data-[p-active=true]:bg-white rounded-full' },
          indicatorButton: {
            class: 'w-3 h-3 rounded-full border-1 border-white bg-transparent transition-colors',
          },
        }"
      >
        <template #item="slotProps">
          <div class="flex h-121.5 flex-col gap-5 rounded-xl bg-neutral-500 p-5">
            <img
              :alt="slotProps.data.quote.location"
              :src="renderImage(`${slotProps.data.quote.imageUrl}`, { quality: 100, width: 260 })"
              class="max-h-50 rounded-xl object-cover object-top"
            />
            <div class="flex h-full flex-col text-center text-white">
              <p class="mb-4 text-lg leading-6">{{ slotProps.data.quote.body }}</p>
              <p class="mb-4">{{ slotProps.data.quote.profession }}</p>
              <p class="text-xs">{{ slotProps.data.quote.location }}</p>
            </div>
          </div>
        </template>
      </Carousel>
      <Carousel
        v-model:page="currentPage"
        :value="fundraiseForIndustries"
        :num-visible="1"
        :num-scroll="1"
        :circular="true"
        :show-indicators="false"
        :show-navigators="false"
        class="w-full"
      >
        <template #item="slotProps">
          <div class="text-center text-white">
            <p class="font-family-display mb-5 text-3xl font-semibold">{{ slotProps.data.name }}</p>
            <p class="text-lg">{{ slotProps.data.description }}</p>
          </div>
        </template>
      </Carousel>
    </div>
  </div>
</template>

<script setup lang="ts">
import Accordion from 'primevue/accordion';
import Carousel from 'primevue/carousel';
import renderImage from '~/utils/render-image';

const { t } = useI18n();

const activeIndex = ref(0);
const lastOpenedIndex = ref(0);
const currentPage = ref(0);

const fundraiseForIndustries = computed(() => [
  {
    name: t('HomeFundraisingFor.community.name'),
    description: t('HomeFundraisingFor.community.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/Alberta-Ballet-Photo.jpg',
      body: t('HomeFundraisingFor.community.quoteBody'),
      profession: t('HomeFundraisingFor.community.quoteProfession'),
      location: t('HomeFundraisingFor.community.quoteLocation'),
    },
    active: true,
    bgColor: 'bg-green-100',
  },
  {
    name: t('HomeFundraisingFor.sports.name'),
    description: t('HomeFundraisingFor.sports.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/Cole-Harbour-Minor-Hockey-photo.jpg',
      body: t('HomeFundraisingFor.sports.quoteBody'),
      profession: t('HomeFundraisingFor.sports.quoteProfession'),
      location: t('HomeFundraisingFor.sports.quoteLocation'),
    },
    bgColor: 'bg-orange-100',
  },
  {
    name: t('HomeFundraisingFor.health.name'),
    description: t('HomeFundraisingFor.health.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/Alberta-Cancer-photo.jpg',
      body: t('HomeFundraisingFor.health.quoteBody'),
      profession: t('HomeFundraisingFor.health.quoteProfession'),
      location: t('HomeFundraisingFor.health.quoteLocation'),
    },
    bgColor: 'bg-rose-100',
  },
  {
    name: t('HomeFundraisingFor.animals.name'),
    description: t('HomeFundraisingFor.animals.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/Hope-photo.jpg',
      body: t('HomeFundraisingFor.animals.quoteBody'),
      profession: t('HomeFundraisingFor.animals.quoteProfession'),
      location: t('HomeFundraisingFor.animals.quoteLocation'),
    },
    bgColor: 'bg-violet-200',
  },
  {
    name: t('HomeFundraisingFor.corporations.name'),
    description: t('HomeFundraisingFor.corporations.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/corporations.jpg',
      body: t('HomeFundraisingFor.corporations.quoteBody'),
    },
    bgColor: 'bg-green-100',
  },
  {
    name: t('HomeFundraisingFor.festivalsAndEvents.name'),
    description: t('HomeFundraisingFor.festivalsAndEvents.description'),
    quote: {
      imageUrl: 'RaffleboxLogos/WebsiteAssets/Theatre-Calgary-photo.jpg',
      body: t('HomeFundraisingFor.festivalsAndEvents.quoteBody'),
      profession: t('HomeFundraisingFor.festivalsAndEvents.quoteProfession'),
      location: t('HomeFundraisingFor.festivalsAndEvents.quoteLocation'),
    },
    bgColor: 'bg-orange-100',
  },
]);

const handleTabOpen = (event: { index: number }) => {
  lastOpenedIndex.value = event.index;
};
</script>
